/**
 * Weekly access schedule mask (VMS-331).
 *
 * A member's schedule is 168 bits — 7 days x 24 one-hour slots — read as local
 * wall-clock time AT THE SERVER, not in the operator's browser zone. This
 * module converts between the wire form (base64 of 21 bytes) and a flat array
 * of 168 booleans the grid renders.
 *
 * WIRE LAYOUT — a cross-repo contract, not an implementation detail. The mask
 * carries nothing self-describing, so cloud, this UI and the media_server must
 * agree bit for bit or every schedule is silently misread:
 *
 *     slot index = (ISO weekday - 1) * 24 + hour     Monday is day 1
 *     slot 0   = Monday 00:00
 *     slot 167 = Sunday 23:00
 *     byte i holds slots 8i..8i+7, MOST SIGNIFICANT BIT FIRST
 *
 * Monday-first matches ISO 8601, the Go side (auth.slotIndex) and the
 * media_server's occurra::auth::Schedule. A Sunday-first mask would enforce
 * "weekdays 08:00-17:00" as Sunday to Thursday with nothing erroring.
 *
 * NULL AND ALL-ZERO ARE DIFFERENT, and the difference is the whole point:
 *
 *     null      no schedule    always allowed
 *     all-zero  never allowed
 *
 * So `null` is represented as null here too, never as an array of 168 falses.
 * A zero-initialised mask has to fail closed; if zero meant "always", a bug
 * that failed to populate it would silently grant unrestricted access.
 */

import type { ScheduleMask } from '@/types/auth';
import type { DisplayFormat } from '@/types/displayFormat';

import { isMaskBitSet, maskByteLength, setMaskBit } from './bitMask';
import { DISPLAY_FORMAT } from './displayFormat';
import { fmtClockSpan, getPartsInZone } from './time';
import { HOUR_MS } from './timeUnits';

export const SLOTS_PER_DAY = 24;
export const DAYS_PER_WEEK = 7;
export const SLOT_COUNT = DAYS_PER_WEEK * SLOTS_PER_DAY; //< 168
export const MASK_BYTES = maskByteLength(SLOT_COUNT);

/**
 * Day labels in wire order: index 0 is Monday, matching slot 0.
 * Not derived from the browser locale — the order is part of the wire format,
 * and a locale that starts the week on Sunday would silently relabel the grid.
 */
export const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const DAY_LABELS_LONG = [
    'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
];

/** Monday..Friday: the first five wire days. */
const WORKDAY_COUNT = 5;
const WORKDAY_START_HOUR = 8;
const WORKDAY_END_HOUR = 17;

/** Slot index for a wire day (0 = Monday) and hour. */
export function getSlotIndex(dayIndex: number, hour: number): number {
    return dayIndex * SLOTS_PER_DAY + hour;
}

/** A mask with every slot closed — "never", which is NOT the same as null. */
export function makeEmptyMask(): ScheduleMask {
    return new Array(SLOT_COUNT).fill(false);
}

/** A mask with every slot open. */
export function getFullMask(): ScheduleMask {
    return new Array(SLOT_COUNT).fill(true);
}

/** Monday–Friday 08:00–17:00, the shape operators reach for most often. */
export function getBusinessHoursMask(): ScheduleMask {
    const slots = makeEmptyMask();
    for (let day = 0; day < WORKDAY_COUNT; day += 1) {
        for (let hour = WORKDAY_START_HOUR; hour < WORKDAY_END_HOUR; hour += 1) {
            slots[getSlotIndex(day, hour)] = true;
        }
    }
    return slots;
}

/**
 * Decode a base64 wire mask into 168 booleans.
 *
 * Returns null for null/undefined — the absence of a schedule, which must stay
 * distinguishable from an all-zero one. A mask of the wrong length is treated
 * as absent rather than padded or truncated: guessing which end to pad would
 * silently widen or narrow someone's access.
 */
export function decodeSchedule(encodedText: string | null): ScheduleMask | null {
    if (encodedText === null || encodedText === undefined) { return null; }

    let raw;
    try {
        raw = atob(encodedText);
    } catch {
        return null;
    }
    if (raw.length !== MASK_BYTES) { return null; }

    const bytes = Uint8Array.from(raw, (char) => char.charCodeAt(0));
    const slots = makeEmptyMask();
    for (let slot = 0; slot < SLOT_COUNT; slot += 1) {
        if (isMaskBitSet(bytes, slot)) { slots[slot] = true; }
    }
    return slots;
}

/**
 * Encode 168 booleans into a base64 wire mask.
 *
 * null in, null out: "no schedule" is not an empty mask.
 */
export function encodeSchedule(slots: ScheduleMask): string;
export function encodeSchedule(slots: ScheduleMask | null): string | null;
export function encodeSchedule(slots: ScheduleMask | null): string | null {
    if (slots === null || slots === undefined) { return null; }

    const bytes = new Uint8Array(MASK_BYTES);
    for (let slot = 0; slot < SLOT_COUNT; slot += 1) {
        if (slots[slot]) { setMaskBit(bytes, slot); }
    }

    let binary = '';
    for (const byte of bytes) { binary += String.fromCharCode(byte); }
    return btoa(binary);
}

/** Whether every slot is closed — a schedule that never admits anyone. */
export function isNeverMask(slots: ScheduleMask | null): boolean {
    return Array.isArray(slots) && slots.every((s) => !s);
}

/** Whether every slot is open — equivalent in effect to no schedule. */
export function isAlwaysMask(slots: ScheduleMask | null): boolean {
    return Array.isArray(slots) && slots.every(Boolean);
}

/**
 * A one-line description of a mask, for the members list.
 *
 * Deliberately does not try to render arbitrary masks as prose — "Mon–Fri,
 * Tue 22:00" is worse than a slot count. It names the shapes an operator will
 * recognise and falls back to counting.
 */
export function describeSchedule(slots: ScheduleMask | null): string {
    if (slots === null || slots === undefined) { return 'Always'; }
    if (isNeverMask(slots)) { return 'Never'; }
    if (isAlwaysMask(slots)) { return 'Always'; }

    const openCount = slots.filter(Boolean).length;
    const business = getBusinessHoursMask();
    if (business.every((v, i) => v === slots[i])) { return 'Weekdays 08:00–17:00'; }

    return `${openCount} ${openCount === 1 ? 'hour' : 'hours'} a week`;
}

/** Monday to Friday, Saturday and Sunday, and the full week, as the hours sentence names them. */
const DAY_GROUP_NAMES: Record<string, string> = Object.freeze({
    '0-4': 'workdays',
    '5-6': 'weekends',
    '0-6': 'every day',
});

/** The open runs of one day, as [start hour, end hour) pairs. */
function getDayRuns(slots: ScheduleMask, dayIndex: number): [number, number][] {
    const runs: [number, number][] = [];
    let start: number | null = null;
    for (let hour = 0; hour <= SLOTS_PER_DAY; hour += 1) {
        const isOpen = hour < SLOTS_PER_DAY && !!slots[getSlotIndex(dayIndex, hour)];
        if (isOpen && start === null) {
            start = hour;
        } else if (!isOpen && start !== null) {
            runs.push([start, hour]);
            start = null;
        }
    }
    return runs;
}

function describeRuns(runs: [number, number][], format: DisplayFormat): string {
    const [onlyValue] = runs;
    if (runs.length === 1 && onlyValue && onlyValue[0] === 0 && onlyValue[1] === SLOTS_PER_DAY) {
        return 'all day';
    }
    // An hour of the week is a wall-clock reading, so it is written as that hour of a UTC day.
    return runs
        .map(([start, end]) => fmtClockSpan(start * HOUR_MS, end * HOUR_MS, 'UTC', { format }))
        .join(' and ');
}

function describeDays(firstValue: number, lastValue: number): string {
    const name = DAY_GROUP_NAMES[`${firstValue}-${lastValue}`];
    if (name !== undefined) { return name; }
    const labels = DAY_LABELS.slice(firstValue, lastValue + 1);
    return labels.length === 1 ? labels.join('') : [labels.at(0), labels.at(-1)].join('–');
}

/**
 * The open hours of a mask as a reader says them: "workdays 9:00 AM – 6:00 PM, weekends all day".
 * Consecutive days with the same hours share one part, and days without open hours are left out.
 */
export function describeScheduleHours(
    slots: ScheduleMask, format: DisplayFormat = DISPLAY_FORMAT,
): string {
    const hoursByDay = Array.from(
        { length: DAYS_PER_WEEK }, (_, day) => describeRuns(getDayRuns(slots, day), format));
    const parts: string[] = [];
    let firstValue = 0;
    hoursByDay.forEach((hours, day) => {
        if (hoursByDay[day + 1] === hours) { return; }
        if (hours !== '') { parts.push(`${describeDays(firstValue, day)} ${hours}`); }
        firstValue = day + 1;
    });
    return parts.length > 0 ? parts.join(', ') : 'never';
}

/** Whether the slot of the instant `ms`, read on the wall clock of `zone`, is open. */
export function isScheduleOpenAt(slots: ScheduleMask, ms: number, zone: string): boolean {
    const wall = getPartsInZone(ms, zone);
    // getUTCDay counts from Sunday; the wire counts from Monday.
    const dayIndex = (new Date(Date.UTC(wall.year, wall.month - 1, wall.day)).getUTCDay() +
        DAYS_PER_WEEK - 1) % DAYS_PER_WEEK;
    return !!slots[getSlotIndex(dayIndex, wall.hour)];
}
